import { Handshake, ShieldCheck, BadgeCheck } from "lucide-react";

/**
 * PartnersGrid — Section "Nos partenaires" premium
 * 6 logos officiels fournis par le client + 2 rendus typographiques
 * (Antargaz, Eni) en attente des visuels officiels.
 */
const PARTNERS = [
  {
    slug: "edf",
    name: "EDF",
    tagline: "Obligé historique",
    logo: "/partners/edf.png",
  },
  {
    slug: "engie",
    name: "ENGIE",
    tagline: "Obligé national",
    logo: "/partners/engie.png",
  },
  {
    slug: "totalenergies",
    name: "TotalEnergies",
    tagline: "Obligé & délégataire",
    logo: "/partners/totalenergies.png",
  },
  {
    slug: "shell",
    name: "Shell",
    tagline: "Obligé énergéticien",
    logo: "/partners/shell.png",
  },
  {
    slug: "esso",
    name: "Esso",
    tagline: "Obligé énergéticien",
    logo: "/partners/esso.png",
  },
  {
    slug: "effy",
    name: "EFFY",
    tagline: "Délégataire",
    logo: "/partners/effy.png",
  },
  {
    slug: "butagaz",
    name: "Butagaz",
    tagline: "Obligé énergéticien",
    logo: "/partners/butagaz.png",
  },
  {
    slug: "primagaz",
    name: "Primagaz",
    tagline: "Obligé énergéticien",
    logo: "/partners/primagaz.png",
  },
  {
    slug: "antargaz",
    name: "Antargaz",
    tagline: "Obligé énergéticien",
    // Rendu typographique premium en attente du logo officiel
    render: () => (
      <div className="flex flex-col items-center" style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
        <span className="text-[30px] font-black tracking-tight text-[#EE1C25] leading-none lowercase">antargaz</span>
        <span className="text-[9px] font-semibold tracking-[0.4em] text-[#0F172A] mt-2">ÉNERGIES</span>
      </div>
    ),
  },
  {
    slug: "eni",
    name: "Eni",
    tagline: "Obligé & délégataire",
    render: () => (
      <div className="flex items-center gap-2.5" style={{ fontFamily: "'Helvetica Neue', Arial, sans-serif" }}>
        <svg viewBox="0 0 40 40" className="h-10 w-10" aria-hidden="true">
          <circle cx="20" cy="20" r="18" fill="#FFCC00" />
          <path d="M12 15 Q 20 8, 28 15 Q 30 22, 20 30 Q 10 22, 12 15 Z" fill="#0F172A" opacity="0.9" />
          <circle cx="20" cy="17" r="2.5" fill="#FFCC00" />
        </svg>
        <span className="text-[36px] font-black tracking-tight text-[#0F172A] lowercase leading-none">eni</span>
      </div>
    ),
  },
];

export default function PartnersGrid() {
  return (
    <section
      data-testid="partners-section"
      className="relative bg-white overflow-hidden"
    >
      {/* Trame très subtile */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.35]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 25% 20%, rgba(16,185,129,0.05) 0%, transparent 45%), radial-gradient(circle at 80% 80%, rgba(15,23,42,0.04) 0%, transparent 50%)",
        }}
      />

      <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-24 md:py-32">
        {/* Header */}
        <div className="max-w-3xl mb-14 md:mb-20">
          <div className="flex items-center gap-3 mb-5">
            <div className="h-10 w-10 rounded-xl bg-[#064E3B] grid place-items-center text-[#10B981]">
              <Handshake className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="text-[11px] font-semibold tracking-[0.28em] text-[#10B981] uppercase">
              Nos partenaires
            </div>
          </div>

          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-[#0F172A] tracking-tight text-balance leading-[1.05]">
            Nous collaborons avec les principaux acteurs de l&apos;énergie en France.
          </h2>
          <p className="mt-6 text-[#475569] md:text-lg leading-relaxed max-w-2xl">
            Grâce à nos accords directs avec les <strong className="text-[#0F172A] font-semibold">énergéticiens obligés</strong> et
            les grands délégataires du dispositif CEE, ECONATION garantit à ses clients les
            <strong className="text-[#0F172A] font-semibold"> meilleurs prix de rachat du marché</strong> et une
            sécurité maximale sur l&apos;instruction des dossiers auprès du PNCEE.
          </p>
        </div>

        {/* Grid — 10 partenaires */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
          {PARTNERS.map((p) => (
            <article
              key={p.slug}
              data-testid={`partner-${p.slug}`}
              className="group relative bg-white border border-black/[0.06] rounded-2xl aspect-[5/3] flex flex-col items-center justify-center p-8 hover:border-[#10B981]/40 hover:shadow-[0_30px_80px_-40px_rgba(15,23,42,0.35)] hover:-translate-y-1 transition-all duration-500"
            >
              <div className="flex-1 flex items-center justify-center w-full max-w-[70%] transition-transform duration-500 group-hover:scale-[1.06]">
                {p.logo ? (
                  <img
                    src={p.logo}
                    alt={`Logo ${p.name}`}
                    loading="lazy"
                    className="max-h-16 md:max-h-20 w-auto object-contain"
                  />
                ) : (
                  p.render()
                )}
              </div>
              <div className="absolute bottom-3 left-0 right-0 text-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <span className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#10B981]">
                  {p.tagline}
                </span>
              </div>
            </article>
          ))}
        </div>

        {/* Bandeau de confiance */}
        <div className="mt-14 md:mt-20 grid md:grid-cols-3 gap-5">
          <div data-testid="trust-card-security" className="bg-[#F8FAFC] rounded-2xl p-7 border border-black/[0.04] hover:border-[#10B981]/30 transition-colors">
            <div className="h-11 w-11 rounded-xl bg-white border border-black/[0.05] grid place-items-center text-[#10B981] mb-4 shadow-sm">
              <ShieldCheck className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="font-display text-lg font-semibold text-[#0F172A] mb-1.5">
              Sécurité juridique totale
            </div>
            <p className="text-sm text-[#475569] leading-relaxed">
              Nos accords-cadres avec les obligés garantissent la traçabilité et la conformité PNCEE de chaque dossier CEE.
            </p>
          </div>

          <div data-testid="trust-card-price" className="bg-[#F8FAFC] rounded-2xl p-7 border border-black/[0.04] hover:border-[#10B981]/30 transition-colors">
            <div className="h-11 w-11 rounded-xl bg-white border border-black/[0.05] grid place-items-center text-[#10B981] mb-4 shadow-sm">
              <BadgeCheck className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="font-display text-lg font-semibold text-[#0F172A] mb-1.5">
              Meilleur prix garanti
            </div>
            <p className="text-sm text-[#475569] leading-relaxed">
              Mise en concurrence systématique de 8+ obligés pour maximiser la prime CEE reversée à votre projet.
            </p>
          </div>

          <div data-testid="trust-card-network" className="bg-[#F8FAFC] rounded-2xl p-7 border border-black/[0.04] hover:border-[#10B981]/30 transition-colors">
            <div className="h-11 w-11 rounded-xl bg-white border border-black/[0.05] grid place-items-center text-[#10B981] mb-4 shadow-sm">
              <Handshake className="h-5 w-5" strokeWidth={1.75} />
            </div>
            <div className="font-display text-lg font-semibold text-[#0F172A] mb-1.5">
              Réseau national
            </div>
            <p className="text-sm text-[#475569] leading-relaxed">
              Une collaboration privilégiée avec les 8 grands acteurs de l&apos;énergie qui financent la transition en France.
            </p>
          </div>
        </div>

        {/* Outro */}
        <div className="mt-16 md:mt-20 text-center max-w-3xl mx-auto">
          <p className="text-[#475569] md:text-lg leading-relaxed">
            <span className="text-[#0F172A] font-semibold">Une confiance construite dossier après dossier.</span> Depuis notre création, ECONATION a valorisé plusieurs dizaines de millions d&apos;euros de primes CEE auprès de ces partenaires — 0 % de redressement, 100 % de dossiers instruits.
          </p>
        </div>
      </div>
    </section>
  );
}
